import { useState, useCallback, useEffect } from 'react';
import { 
  Position, 
  GameState, 
  PlacedWord, 
  GameGrid,
  WORD_COUNT,
  areAdjacent,
  getWordFromPath,
  positionsEqual
} from '../types';
import { generateGameGrid, checkWordMatch, doesPathMatchWord } from '../utils/gridGenerator';

interface UseGameStateReturn extends GameState {
  handleCellMouseDown: (pos: Position) => void;
  handleCellMouseEnter: (pos: Position) => void;
  handleMouseUp: () => void;
  resetGame: () => void;
  dismissWinModal: () => void;
  celebrationWord: string | null;
  clearCelebration: () => void;
  giveUp: () => void;
  gaveUp: boolean;
  allHiddenWords: PlacedWord[];
  failedAttempts: string[];
}

const createInitialState = (): Omit<GameState, 'grid' | 'hiddenWords'> & { 
  grid: GameGrid | null; 
  hiddenWords: PlacedWord[] 
} => ({
  grid: null,
  hiddenWords: [],
  foundWords: [],
  currentSelection: [],
  isSelecting: false,
  gameOver: false,
  showWinModal: false
});

export function useGameState(): UseGameStateReturn {
  const [state, setState] = useState<GameState & { hiddenWords: PlacedWord[] }>(() => {
    const result = generateGameGrid(WORD_COUNT);
    if (result) {
      return {
        grid: result.grid,
        hiddenWords: result.placedWords,
        foundWords: [],
        currentSelection: [],
        isSelecting: false,
        gameOver: false,
        showWinModal: false
      };
    }
    // Fallback - this shouldn't happen in normal circumstances
    return createInitialState() as GameState & { hiddenWords: PlacedWord[] };
  });

  const [foundWordPaths, setFoundWordPaths] = useState<PlacedWord[]>([]);
  const [celebrationWord, setCelebrationWord] = useState<string | null>(null);
  const [gaveUp, setGaveUp] = useState<boolean>(false);
  const [failedAttempts, setFailedAttempts] = useState<string[]>([]);

  const resetGame = useCallback(() => {
    const result = generateGameGrid(WORD_COUNT);
    if (result) {
      setState({
        grid: result.grid,
        hiddenWords: result.placedWords,
        foundWords: [],
        currentSelection: [],
        isSelecting: false,
        gameOver: false,
        showWinModal: false
      });
      setFoundWordPaths([]);
      setCelebrationWord(null);
      setGaveUp(false);
      setFailedAttempts([]);
    }
  }, []);

  const handleCellMouseDown = useCallback((pos: Position) => {
    if (state.gameOver) return;

    setState(prev => ({
      ...prev,
      isSelecting: true,
      currentSelection: [pos]
    }));
  }, [state.gameOver]);

  const handleCellMouseEnter = useCallback((pos: Position) => {
    if (!state.isSelecting || state.gameOver) return;

    setState(prev => {
      const lastPos = prev.currentSelection[prev.currentSelection.length - 1];
      
      // If not adjacent to last position, don't add
      if (!lastPos || !areAdjacent(lastPos, pos)) {
        return prev;
      }

      // If already in selection (going backwards), allow it for zig-zag
      const existingIndex = prev.currentSelection.findIndex(p => positionsEqual(p, pos));
      if (existingIndex !== -1) {
        // Truncate selection back to this point
        return {
          ...prev,
          currentSelection: prev.currentSelection.slice(0, existingIndex + 1)
        };
      }

      return {
        ...prev,
        currentSelection: [...prev.currentSelection, pos]
      };
    });
  }, [state.isSelecting, state.gameOver]);

  const handleMouseUp = useCallback(() => {
    if (!state.isSelecting || state.gameOver || !state.grid) {
      setState(prev => ({
        ...prev,
        isSelecting: false,
        currentSelection: []
      }));
      return;
    }

    // Get the selected word
    const selectedWord = getWordFromPath(state.grid, state.currentSelection);
    
    // Check if it matches any hidden word
    const matchedWord = checkWordMatch(selectedWord, state.hiddenWords, state.foundWords);
    
    if (matchedWord && doesPathMatchWord(state.currentSelection, matchedWord.path)) {
      // Found a word!
      const newFoundWords = [...state.foundWords, matchedWord.word];
      const isGameWon = newFoundWords.length === WORD_COUNT;
      
      // Show celebration
      setCelebrationWord(matchedWord.word);
      
      // Add to found paths
      setFoundWordPaths(prev => [...prev, matchedWord]);
      
      setState(prev => ({
        ...prev,
        foundWords: newFoundWords,
        currentSelection: [],
        isSelecting: false,
        gameOver: isGameWon,
        showWinModal: isGameWon
      }));
    } else {
      // Invalid selection - track as failed attempt if it's at least 2 letters
      const selectedWord = getWordFromPath(state.grid, state.currentSelection);
      if (selectedWord.length >= 2) {
        setFailedAttempts(prev => {
          // Don't add duplicates
          if (prev.includes(selectedWord)) return prev;
          return [...prev, selectedWord];
        });
      }
      
      setState(prev => ({
        ...prev,
        currentSelection: [],
        isSelecting: false
      }));
    }
  }, [state.isSelecting, state.gameOver, state.grid, state.hiddenWords, state.foundWords, state.currentSelection]);

  const dismissWinModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      showWinModal: false
    }));
    // Auto-restart game when modal is dismissed
    resetGame();
  }, [resetGame]);

  const clearCelebration = useCallback(() => {
    setCelebrationWord(null);
  }, []);

  const giveUp = useCallback(() => {
    // Mark game as over and reveal all words
    setGaveUp(true);
    setState(prev => ({
      ...prev,
      gameOver: true,
      isSelecting: false,
      currentSelection: []
    }));
  }, []);

  // Ensure grid is generated on mount
  useEffect(() => {
    if (!state.grid) {
      resetGame();
    }
  }, [state.grid, resetGame]);

  return {
    ...state,
    grid: state.grid!,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleMouseUp,
    resetGame,
    dismissWinModal,
    celebrationWord,
    clearCelebration,
    giveUp,
    gaveUp,
    failedAttempts,
    // Override found words to use paths for highlighting
    foundWords: state.foundWords,
    // Expose found word paths for the grid component
    hiddenWords: foundWordPaths.length > 0 ? foundWordPaths : state.hiddenWords.filter(hw => state.foundWords.includes(hw.word)),
    // Expose all hidden words for give up feature
    allHiddenWords: state.hiddenWords
  };
}
